import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMembershipService } from 'services/memberships';
import type { MembershipStatistics as StatsType } from 'types/membership';
import Button from 'components/Button';
import RefreshButton from 'components/RefreshButton';
import { ROUTES } from 'utils/constants';
import { ArrowLeft } from 'lucide-react';
import styles from '../Membership.module.scss';

function formatLabel(key: string): string {
  if (key === 'unspecified' || key === 'unknown') return key;
  return key.replace(/-/g, '–').replace(/\b\w/g, (c) => c.toUpperCase());
}

function BreakdownSection({
  title,
  data,
  labelFilter,
}: {
  title: string;
  data: Record<string, number>;
  labelFilter?: string;
}) {
  const q = labelFilter?.trim().toLowerCase() ?? '';
  const entries = Object.entries(data)
    .filter(([, count]) => count > 0)
    .filter(([key]) => !q || formatLabel(key).toLowerCase().includes(q));
  if (entries.length === 0) return null;
  return (
    <div className={styles.statsSection}>
      <h3 className={styles.statsSectionTitle}>{title}</h3>
      <ul className={styles.statsBreakdownList}>
        {entries.map(([key, count]) => (
          <li key={key} className={styles.statsBreakdownItem}>
            <span className={styles.statsBreakdownLabel}>
              {formatLabel(key)}
            </span>
            <span className={styles.statsBreakdownCount}>{count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MembershipStatistics() {
  const navigate = useNavigate();
  const membershipService = useMembershipService();
  const [stats, setStats] = useState<StatsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statsLabelFilter, setStatsLabelFilter] = useState('');

  const loadStats = useCallback(
    async (isReload = false) => {
      if (!isReload) setLoading(true);
      setError(null);
      try {
        const data = await membershipService.getMembershipStatistics();
        setStats(data);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to load membership statistics.',
        );
      } finally {
        setLoading(false);
      }
    },
    [membershipService],
  );

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Membership statistics</h1>
        <div className={styles.headerButtons}>
          <Button
            variant="outline"
            onClick={() => navigate(ROUTES.MEMBERSHIP.ROOT)}
            aria-label="Back to Member List"
          >
            <ArrowLeft size={18} strokeWidth={2} />
            Back
          </Button>
          <RefreshButton
            onClick={() => void loadStats(true)}
            loading={loading}
          />
        </div>
      </header>

      {loading && !stats && (
        <div className={styles.loadingState}>
          <p>Loading statistics...</p>
        </div>
      )}

      {error && !stats && (
        <div className={styles.errorState}>
          <p>{error}</p>
          <Button onClick={() => void loadStats()}>Retry</Button>
        </div>
      )}

      {!loading && stats && (
        <>
          <div className={styles.statsSummary}>
            <div className={styles.statsCard}>
              <span className={styles.statsCardLabel}>
                Total active members
              </span>
              <span className={styles.statsCardValue}>
                {stats.totalMembers}
              </span>
            </div>
            <div className={styles.statsCard}>
              <span className={styles.statsCardLabel}>
                New members this month
              </span>
              <span className={styles.statsCardValue}>
                {stats.newMembersThisMonth}
              </span>
            </div>
            <div className={styles.statsCard}>
              <span className={styles.statsCardLabel}>
                New members this year
              </span>
              <span className={styles.statsCardValue}>
                {stats.newMembersThisYear}
              </span>
            </div>
          </div>

          <div className={styles.columnFilterControls}>
            <div className={styles.searchInputWrap}>
              <input
                type="search"
                className={styles.searchInput}
                value={statsLabelFilter}
                onChange={(e) => setStatsLabelFilter(e.target.value)}
                placeholder="Filter breakdown rows..."
                aria-label="Filter statistics breakdown rows"
              />
            </div>
          </div>

          <div className={styles.statsGrid}>
            <BreakdownSection
              title="By status"
              data={stats.byStatus}
              labelFilter={statsLabelFilter}
            />
            <BreakdownSection
              title="By membership type"
              data={stats.byMembershipType}
              labelFilter={statsLabelFilter}
            />
            <BreakdownSection
              title="By gender"
              data={stats.byGender}
              labelFilter={statsLabelFilter}
            />
            <BreakdownSection
              title="By civil status"
              data={stats.byCivilStatus}
              labelFilter={statsLabelFilter}
            />
            <BreakdownSection
              title="By age group"
              data={stats.byAgeGroup}
              labelFilter={statsLabelFilter}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default MembershipStatistics;
